import React, { useEffect, useRef, useState } from "react";

import { ButtonStandard, Checkbox, ModalBase, Text } from "@performyard/py-components";

export type EndRecurringSeriesConfirmOptions = {
	removeCalendarEvents?: boolean;
};

type EndRecurringSeriesModalProps = {
	isOpen: boolean;
	isLoading: boolean;
	seriesName: string | undefined;
	/** A series that already reached its end date is being removed, not stopped. */
	isSeriesEnded: boolean;
	/** Offers the choice to remove the series' future calendar events; a finished series has none left. */
	hasCalendarSeries: boolean;
	onClose: () => void;
	onConfirm: (options: EndRecurringSeriesConfirmOptions) => void;
};

export const EndRecurringSeriesModal = ({
	isOpen,
	isLoading,
	seriesName,
	isSeriesEnded,
	hasCalendarSeries,
	onClose,
	onConfirm,
}: EndRecurringSeriesModalProps): React.ReactElement => {
	const [removeCalendarEvents, setRemoveCalendarEvents] = useState(false);

	// Snapshot the copy inputs so they don't flicker during the close animation
	const contentRef = useRef<{ seriesName: string | undefined; isSeriesEnded: boolean; hasCalendarSeries: boolean }>({
		seriesName: undefined,
		isSeriesEnded: false,
		hasCalendarSeries: false,
	});

	useEffect(() => {
		if (isOpen) {
			contentRef.current = { seriesName, isSeriesEnded, hasCalendarSeries };
			setRemoveCalendarEvents(false);
		}
	}, [isOpen, seriesName, isSeriesEnded, hasCalendarSeries]);

	const displaySeriesName = isOpen ? seriesName : contentRef.current.seriesName;
	const displayIsSeriesEnded = isOpen ? isSeriesEnded : contentRef.current.isSeriesEnded;
	const displayHasCalendarSeries = isOpen ? hasCalendarSeries : contentRef.current.hasCalendarSeries;
	const actionLabel = displayIsSeriesEnded ? "Remove Recurring Meeting" : "End Recurring Meeting";

	return (
		<ModalBase isOpen={isOpen} onClose={isLoading ? () => {} : onClose} dangerouslySetClassName="w-[432px]">
			<div className="flex flex-col items-center gap-4 px-6 pb-6 text-center">
				<div className="flex flex-col items-center">
					<Text size="sub-heading" weight="semi-bold">
						{actionLabel}?
					</Text>
					{displaySeriesName && (
						<Text size="paragraph-2" weight="regular" color="neutral-6">
							{displaySeriesName}
						</Text>
					)}
					<Text size="paragraph-2" weight="regular">
						{displayIsSeriesEnded
							? "This series has already finished, so removing it frees up a recurring meeting slot in this workspace. Any meetings created in PerformYard will remain as is."
							: "New meetings for this series will stop being created. Any meetings created in PerformYard will remain as is."}
					</Text>
				</div>
				{displayHasCalendarSeries && !displayIsSeriesEnded && (
					<div className="flex items-center gap-2">
						<Checkbox
							id="remove-series-calendar-events"
							checked={removeCalendarEvents}
							onCheckedChange={setRemoveCalendarEvents}
							disabled={isLoading}
							aria-label="Remove future events from connected calendars"
						/>
						<Text size="paragraph-3" weight="regular">
							Remove future events from connected calendars
						</Text>
					</div>
				)}
			</div>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={onClose}
						disabled={isLoading}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="solid"
						color="red"
						size="large"
						onClick={() => onConfirm({ removeCalendarEvents })}
						disabled={isLoading}
						data-testid="confirm-end-series-button"
					>
						{actionLabel}
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};
